import { useQuery } from '@tanstack/react-query'
import { Alert, ConfigProvider } from 'antd'
import { useEffect, useRef, type JSX } from 'react'
import { Redirect, Route, Switch, useLocation, useParams } from 'react-router'
import styled from 'styled-components'

import { findActiveSection, getClientSectionGroups, OVERVIEW_SECTION } from './clientSections'
import { PAGE_BG } from './colors'
import { Amb } from './components/Amb'
import { Billing } from './components/Billing/Billing'
import { ClientFeatureGroups } from './components/ClientFeatureGroups'
import { ClientHeader } from './components/ClientHeader'
import { ClientNav } from './components/ClientNav'
import { CustomDomain } from './components/CustomDomain'
import { General } from './components/General'
import { Keywords } from './components/Keywords'
import { MembersCompliance } from './components/MembersCompliance'
import { Messages } from './components/Messages'
import { NumberImports } from './components/NumberImports'
import { NumberProvisioning } from './components/NumberProvisioning'
import { Overview } from './components/Overview'
import { useClientHealth } from './components/Overview/useClientHealth'
import { SignUp } from './components/SignUp'
import { TCR } from './components/TCR'
import { Team } from './components/Team'
import { CLIENT_PAGE_THEME } from './theme'

import { getClientFeatureGroups } from 'api/capabilities'
import { Client as ClientType, getClient } from 'api/clients'
import { CAPABILITIES } from 'constants/capabilities'
import { isClientFiltered } from 'constants/filteredClients'
import { TOGGLE_CUSTOM_DOMAIN_TAB } from 'constants/launch-darkly-flags'
import { QUERIES } from 'constants/queries'
import { ROUTES } from 'constants/routes'
import { useHasFlag } from 'contexts/LaunchDarklyProvider'
import { useHasCapability } from 'hooks/useCapabilities'
import { BodySpinner } from 'shared/components/BodySpinner'
import { SidebarLayout } from 'shared/layouts'
import { route } from 'utils/router'

const StyledPage = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: ${PAGE_BG};
`

const StyledBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px 32px;
  padding: 24px 32px 64px;
`

const StyledNavColumn = styled.div`
  position: sticky;
  top: 24px;
  flex: 0 0 220px;
  max-width: 100%;

  @media (max-width: 900px) {
    position: static;
    flex-basis: 100%;
  }
`

const StyledContent = styled.div`
  flex: 999 1 520px;
  min-width: 0;
`

const StyledAlert = styled(Alert)`
  margin: 24px 32px;
`

type ClientSettingsProps = {
  clientId: string
  client: ClientType
}

function ClientSettings({ clientId, client }: ClientSettingsProps): JSX.Element {
  const { pathname } = useLocation()
  const canViewClientFeatureGroups = useHasCapability(CAPABILITIES.ADMIN.CLIENT_FEATURE_GROUP.READ)
  const hasCustomDomainCapability = useHasCapability(CAPABILITIES.ADMIN.CUSTOM_DOMAIN.WRITE)
  const canManageCustomDomain = useHasFlag(TOGGLE_CUSTOM_DOMAIN_TAB) && hasCustomDomainCapability
  const { data: clientHasAmb } = useQuery([QUERIES.FEATURE_GROUPS, clientId], () => getClientFeatureGroups(clientId), {
    select: (data) =>
      data.some((featureGroup) => featureGroup.capabilities.some((cap) => cap.name === CAPABILITIES.FEATURE.AMB.ALL)),
  })
  const health = useClientHealth(client)

  const groups = getClientSectionGroups({
    canManageCustomDomain,
    canViewClientFeatureGroups,
    hasAmb: Boolean(clientHasAmb),
    isCommunityCSPClient: health.isCommunityCSPClient,
    isNotTwilio: health.isNotTwilio,
  })
  const activeSection = findActiveSection(pathname, clientId, [
    OVERVIEW_SECTION,
    ...groups.flatMap((group) => group.sections),
  ])
  const clientRoute = (path: string) => route(path, { id: clientId })

  return (
    <>
      <ClientHeader
        accountStatus={health.billingClient.data?.accountStatus}
        client={client}
        sectionLabel={activeSection?.label}
      />
      <StyledBody>
        <StyledNavColumn>
          <ClientNav
            attentionCount={health.attentionItems.length}
            clientId={clientId}
            groups={groups}
            sectionBadges={health.sectionBadges}
            selectedPath={activeSection?.path}
          />
        </StyledNavColumn>
        <StyledContent>
          <Switch>
            <Route exact path={clientRoute(ROUTES.CLIENT.OVERVIEW)}>
              <Overview client={client} health={health} />
            </Route>
            <Route exact path={clientRoute(ROUTES.CLIENT.GENERAL)} render={() => <General client={client} />} />
            <Route
              exact
              path={clientRoute(ROUTES.CLIENT.MEMBERS_COMPLIANCE)}
              render={() => <MembersCompliance client={client} />}
            />
            <Route exact path={clientRoute(ROUTES.CLIENT.SIGN_UP)} render={() => <SignUp client={client} />} />
            <Route exact path={clientRoute(ROUTES.CLIENT.TEAM)} render={() => <Team client={client} />} />
            <Route exact path={clientRoute(ROUTES.CLIENT.MESSAGES)} render={() => <Messages client={client} />} />
            <Route exact path={clientRoute(ROUTES.CLIENT.KEYWORDS)} render={() => <Keywords client={client} />} />
            <Route exact path={clientRoute(ROUTES.CLIENT.BILLING)} render={() => <Billing client={client} />} />
            {canManageCustomDomain && (
              <Route exact path={clientRoute(ROUTES.CLIENT.CUSTOM_DOMAIN)}>
                <CustomDomain client={client} whiteLabelEnabled={client.whiteLabelEnabled ?? false} />
              </Route>
            )}
            <Route
              exact
              path={clientRoute(ROUTES.CLIENT.FEATURE_GROUPS)}
              render={() => <ClientFeatureGroups client={client} />}
            />
            <Route exact path={clientRoute(ROUTES.CLIENT.AMB)} render={() => <Amb client={client} />} />
            <Route path={clientRoute(ROUTES.CLIENT.NUMBER_IMPORT)}>
              <NumberImports client={client} />
            </Route>
            {health.isNotTwilio && (
              <Route path={clientRoute(ROUTES.CLIENT.NUMBER_PROVISIONING)}>
                <NumberProvisioning client={client} />
              </Route>
            )}
            {health.isCommunityCSPClient && (
              <Route exact path={clientRoute(ROUTES.CLIENT.TCR)}>
                <TCR client={client} />
              </Route>
            )}
            <Redirect to={clientRoute(ROUTES.CLIENT.OVERVIEW)} />
          </Switch>
        </StyledContent>
      </StyledBody>
    </>
  )
}

export function Client(): JSX.Element | null {
  const { id: clientId } = useParams<{ id: string }>()
  const { pathname } = useLocation()
  const pageRef = useRef<HTMLDivElement>(null)
  const { isLoading, isError, data: client } = useQuery([QUERIES.CLIENT, { id: clientId }], () => getClient(clientId))

  // All sections share this scroll container, so a new section must start at the top.
  useEffect(() => {
    pageRef.current?.scrollTo?.({ top: 0 })
  }, [pathname])

  if (isClientFiltered(clientId)) return null

  return (
    <SidebarLayout>
      <StyledPage ref={pageRef}>
        <ConfigProvider theme={CLIENT_PAGE_THEME}>
          {isLoading && <BodySpinner />}
          {isError && !client && (
            <StyledAlert message="Could not load this client. Please try again." showIcon type="error" />
          )}
          {client && <ClientSettings client={client} clientId={clientId} />}
        </ConfigProvider>
      </StyledPage>
    </SidebarLayout>
  )
}
